import React, { useEffect, useRef, useState } from "react";
import { Search } from "lucide-react";
import { assignPatient, searchPatients, getAnalysis } from "@/lib/patientDocs";
import { T, rowPatientBits, fmtMDY, displayName } from "./pdUtil";
import { CreatePatientBlock } from "./pdCreatePatient";

// Inline "link a patient" panel (spec §1–5). Renders under a Needs-a-patient row in
// Administrative view. Reuses the same calls as the workspace: pg-patient-search + /assign.
function CandRow({ p, pct, active, onPick, testid }) {
  const name = displayName(p.full_name || `${p.first_name || ""} ${p.last_name || ""}`.trim()) || "—";
  return (
    <button type="button" role="radio" aria-checked={active} data-testid={testid} onClick={onPick}
      className="w-full flex items-center gap-3 px-3 rounded-[8px] text-left" data-needs-candidate
      style={{ minHeight: 40, border: `1px solid ${active ? T.indigo : T.hair}`, background: active ? "#F4F4FD" : T.card }}>
      <span style={{ width: 14, height: 14, borderRadius: 999, flex: "none", border: `${active ? 4.5 : 1.5}px solid ${active ? T.indigo : T.mute}`, transition: "border-width .12s" }} />
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-semibold truncate" style={{ color: T.ink }}>{name}</span>
        <span className="block text-[12px] truncate" style={{ color: T.sub }}>MRN {p.mrn || "—"} · DOB {fmtMDY(p.dob) || "—"}</span>
      </span>
      {pct != null ? <span className="text-[12px] font-semibold" style={{ color: T.amberTxt }}>{pct}% match</span> : null}
    </button>
  );
}

function Compare({ docName, docDob, pick }) {
  if (!pick) return <div className="text-[12px]" style={{ color: T.mute }}>Choose a patient to compare the name and date of birth on the document with revQ.</div>;
  const cName = displayName(pick.full_name || `${pick.first_name || ""} ${pick.last_name || ""}`.trim());
  const cDob = fmtMDY(pick.dob);
  const nameDiff = docName && cName && docName.toLowerCase() !== cName.toLowerCase();
  const dobDiff = docDob && cDob && fmtMDY(docDob) !== cDob;
  const Cell = ({ v, diff }) => <span className="text-[12.5px] font-semibold" style={{ color: diff ? T.amberTxt : T.ink }}>{v || "—"}</span>;
  return (
    <div data-testid="needs-compare">
      <div className="grid grid-cols-3 gap-2 text-[10px] uppercase tracking-[.04em] mb-1" style={{ color: T.mute }}><span /><span>On the document</span><span>In revQ</span></div>
      <div className="grid grid-cols-3 gap-2 items-center py-1"><span className="text-[11.5px]" style={{ color: T.sub }}>Name</span><Cell v={docName} diff={nameDiff} /><Cell v={cName} diff={nameDiff} /></div>
      <div className="grid grid-cols-3 gap-2 items-center py-1"><span className="text-[11.5px]" style={{ color: T.sub }}>Date of birth</span><Cell v={fmtMDY(docDob)} diff={dobDiff} /><Cell v={cDob} diff={dobDiff} /></div>
      {(nameDiff || dobDiff) ? <div className="text-[11.5px] mt-1" style={{ color: T.amberTxt }}>Name and date of birth differ. Confirm only if you are sure.</div> : null}
    </div>
  );
}

export function NeedsLinkPanel({ row, clientId, onClose, onOpenDoc, onLinked, onCreated }) {
  const match = row.edi_client_match || {};
  const tentative = match.status === "tentative";
  const alts = Array.isArray(match.alternatives) ? match.alternatives : [];
  const [analysis, setAnalysis] = useState(null);
  const extraAlts = (!alts.length && Array.isArray(analysis?.edi_client_match?.alternatives)) ? analysis.edi_client_match.alternatives : [];
  const allAlts = alts.length ? alts : extraAlts;
  const pb = rowPatientBits(row);
  const wrapRef = useRef(null);
  const docType = analysis?.document_type || analysis?.classification?.document_type || row.document_type;
  const isCard = docType === "government_id" || docType === "id_card";

  const [picked, setPicked] = useState(null); // { patient, fromCand }
  const [q, setQ] = useState(tentative ? "" : [pb.name, pb.dob].filter(Boolean).join(" "));
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [create, setCreate] = useState(false); // inline create-patient mode (§6)

  // Top candidate pre-selected on open (possible-match only).
  useEffect(() => {
    if (tentative && alts.length) setPicked({ patient: alts[0], fromCand: true });
  }, []); // eslint-disable-line

  // Fetch the full analysis once: thin rows omit Lens's alternatives (parity for
  // no_match rows) and create mode needs the extraction blob to prefill the form.
  useEffect(() => {
    getAnalysis(row.id).then(setAnalysis).catch(() => {});
  }, []); // eslint-disable-line

  const candMrns = new Set(allAlts.map((a) => String(a.mrn)));
  const doSearch = async (nameQ, dobQ) => {
    setSearching(true); setError("");
    try {
      const calls = [];
      if (nameQ && nameQ.trim()) calls.push(searchPatients({ client_id: clientId, q: nameQ.trim() }));
      if (dobQ && dobQ.trim()) calls.push(searchPatients({ client_id: clientId, q: dobQ.trim() }));
      const outs = await Promise.all(calls.length ? calls : [searchPatients({ client_id: clientId, q: (nameQ || "").trim() })]);
      const byMrn = new Map();
      outs.forEach((d) => (d?.results || []).forEach((p) => { const k = String(p.mrn || p.chart_id); if (!byMrn.has(k) && !candMrns.has(String(p.mrn))) byMrn.set(k, p); }));
      setResults([...byMrn.values()].slice(0, 3));
    } catch { setResults([]); } finally { setSearching(false); }
  };

  // No-chart-match rows search immediately on open.
  useEffect(() => {
    if (!tentative) doSearch(pb.name, pb.dob);
  }, []); // eslint-disable-line

  const runManual = () => doSearch(q, "");

  const link = async () => {
    if (!picked) return;
    const p = picked.patient;
    setBusy(true); setError("");
    try {
      await assignPatient(row.id, {
        chart_id: p.chart_id || null, chart_uuid: p.chart_uuid || null, mrn: p.mrn || null,
        full_name: p.full_name || `${p.first_name || ""} ${p.last_name || ""}`.trim(), dob: p.dob || null,
        gender: p.gender || null, source: p.source || "postgres",
      });
      const nm = displayName(p.full_name || `${p.first_name || ""} ${p.last_name || ""}`.trim());
      setLeaving(true);
      setTimeout(() => onLinked?.(nm), 220);
    } catch {
      setBusy(false);
      setError("Couldn't link this document. Try again.");
    }
  };

  // Inline create success (§6): slide the row out and advance, same as linking.
  const onCreateDone = () => { setLeaving(true); setTimeout(() => onCreated?.(), 220); };

  // Keyboard (§5): ↑/↓ move between candidates, Enter links, Esc closes.
  const pool = [...allAlts, ...(results || [])];
  const onKey = (e) => {
    if (e.target.tagName === "INPUT") { if (e.key === "Escape") onClose?.(); return; }
    // Let footer/search buttons fire their own click; only candidate radios take arrow/Enter nav.
    if (e.target.tagName === "BUTTON" && !e.target.closest("[data-needs-candidate]")) { if (e.key === "Escape") onClose?.(); return; }
    if (e.key === "Escape") { e.preventDefault(); onClose?.(); return; }
    if (e.key === "Enter") { e.preventDefault(); if (picked && !busy) link(); return; }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      if (!pool.length) return;
      e.preventDefault();
      const idx = pool.findIndex((p) => String(p.mrn || p.chart_id) === String(picked?.patient?.mrn || picked?.patient?.chart_id));
      let n = e.key === "ArrowDown" ? idx + 1 : idx - 1;
      if (n < 0) n = pool.length - 1; if (n >= pool.length) n = 0;
      const nx = pool[n];
      setPicked({ patient: nx, fromCand: candMrns.has(String(nx.mrn)) });
    }
  };
  useEffect(() => { wrapRef.current?.focus?.(); }, []);

  const pickedName = picked ? (displayName(picked.patient.full_name || `${picked.patient.first_name || ""} ${picked.patient.last_name || ""}`.trim()) || "patient") : "";
  const primaryLabel = !picked ? "Choose a patient" : picked.fromCand ? `Confirm ${pickedName}` : `Link to ${pickedName}`;

  // §6 — Create patient in place: the same expanded row becomes the create form (one column, no workspace).
  if (create) {
    return (
      <div className={`pd-needsrow pd-scope${leaving ? " leaving" : ""}`} data-testid={`needs-panel-${row.id}`} id={`needs-panel-${row.id}`}
        ref={wrapRef} tabIndex={-1} onClick={(e) => e.stopPropagation()}>
        <div className="pd-np-inner">
          <div className="pd-np-card pd-np-create">
            <div style={{ padding: "14px 16px" }}>
              <CreatePatientBlock analysisId={row.id} clientId={clientId} extraction={analysis?.extraction} isCard={isCard}
                focusFirst onOpenDoc={onOpenDoc} onCancel={() => setCreate(false)} onDone={onCreateDone} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`pd-needsrow pd-scope${leaving ? " leaving" : ""}`} data-testid={`needs-panel-${row.id}`} id={`needs-panel-${row.id}`}
      ref={wrapRef} tabIndex={-1} onKeyDown={onKey} onClick={(e) => e.stopPropagation()}>
      <div className="pd-np-inner">
        <div className="pd-np-card">
          {/* left: find the patient */}
          <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
            <div className="text-[12.5px]" style={{ color: tentative ? T.amberTxt : T.sub }} data-testid="needs-status-line">
              <span style={{ color: T.amberDot }}>●</span>{" "}
              {tentative
                ? <>Possible match · Lens isn't sure this is the right patient. Check the match, or search.</>
                : <>No chart match · Lens found no patient for {pb.name || "this document"}{pb.dob ? ` · DOB ${pb.dob}` : ""} in client {clientId}.</>}
              {!pb.dob ? <> No date of birth on this document, so check carefully.</> : null}
            </div>

            {allAlts.length ? (
              <div role="radiogroup" aria-label="Lens's matches" className="space-y-1.5">
                <div className="text-[9.5px] font-bold uppercase tracking-[.04em]" style={{ color: T.mute }}>{tentative ? (allAlts.length > 1 ? "Lens's matches" : "Lens's match") : "Lens's possible matches"}</div>
                {allAlts.map((p) => <CandRow key={p.chart_id || p.mrn} p={p} pct={p.confidence != null ? Math.round(p.confidence * 100) : null}
                  active={picked && picked.patient === p} onPick={() => setPicked({ patient: p, fromCand: true })} testid={`needs-candidate-${p.mrn || p.chart_id}`} />)}
              </div>
            ) : null}

            <div>
              <div className="flex items-center gap-2">
                <div className="flex-1 relative"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: T.mute }} />
                  <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runManual()} placeholder="Search name, MRN or DOB" data-testid="needs-search-input"
                    style={{ width: "100%", height: 32, paddingLeft: 32, paddingRight: 10, borderRadius: 9, border: `1px solid ${T.hair}`, fontSize: 13, outline: "none" }} />
                </div>
                <button type="button" data-testid="needs-search" onClick={runManual} className="h-8 px-3 rounded-[9px] text-[12.5px] font-semibold" style={{ border: `1px solid ${T.hair}`, color: T.ink, background: T.card }}>{searching ? "…" : "Search"}</button>
              </div>
              {results != null ? (
                results.length ? (
                  <div role="radiogroup" aria-label="Search results" className="space-y-1.5 mt-2">
                    {results.map((p) => <CandRow key={p.chart_id || p.mrn} p={p} active={picked && picked.patient === p} onPick={() => setPicked({ patient: p, fromCand: false })} testid={`needs-candidate-${p.mrn || p.chart_id}`} />)}
                  </div>
                ) : <div className="text-[12px] mt-2" style={{ color: T.mute }}>No patient in client {clientId} matches “{q}”. Create the patient from the document.</div>
              ) : null}
            </div>
          </div>

          {/* right: check the match */}
          <div style={{ padding: "14px 16px", background: "#FCFCFD", borderLeft: `1px solid ${T.hair}` }}>
            <div className="text-[9.5px] font-bold uppercase tracking-[.04em] mb-2" style={{ color: T.mute }}>Check the match</div>
            <Compare docName={pb.name} docDob={pb.dob} pick={picked?.patient} />
          </div>

          {/* footer */}
          <div className="pd-np-foot flex items-center gap-3" style={{ padding: "10px 16px" }}>
            <button type="button" data-testid="needs-open-document" onClick={onOpenDoc} className="text-[12.5px] font-semibold" style={{ color: T.indigo }}>Open document</button>
            <span className="text-[11px]" style={{ color: T.mute }}>↑ ↓ move · ↵ link</span>
            <span className="flex-1" />
            <button type="button" data-testid="needs-create-patient" onClick={() => setCreate(true)} className="h-8 px-3 rounded-[9px] text-[12.5px] font-semibold" style={{ border: `1px solid ${T.hair}`, color: T.ink, background: T.card }}>Create patient →</button>
            <button type="button" data-testid="needs-link" onClick={link} disabled={!picked || busy}
              className="h-8 px-3.5 rounded-[9px] text-[12.5px] font-semibold text-white" style={{ background: picked ? T.ink : "#C9C9CE", cursor: picked ? "pointer" : "default" }}>{busy ? "Linking…" : primaryLabel}</button>
          </div>
          {error ? <div className="pd-np-foot flex items-center gap-2 text-[12px]" style={{ padding: "0 16px 10px", color: T.red }} data-testid="needs-link-error"><span style={{ width: 6, height: 6, borderRadius: 999, background: T.red }} />{error}</div> : null}
        </div>
      </div>
    </div>
  );
}
